import { z } from 'zod';
import { evaluateCondition } from './conditions.js';
import { conditionSchema, type Condition } from './workflow.js';

/**
 * Типы заявок настраиваются администратором: набор полей, обязательность, маршрут
 * по умолчанию и шаблоны документов. Встроенные типы (командировка, отпуск) добавляют
 * расчёты, которые нельзя задать полями: дни, выходные, суточные, итог сметы.
 */

const key = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z][a-zA-Z0-9_]*$/, 'Латиница и цифры, с буквы');

export const FIELD_TYPES = ['text', 'textarea', 'number', 'digits', 'money', 'date', 'select', 'user', 'department', 'checkbox', 'document', 'table'] as const;
export type FieldType = (typeof FIELD_TYPES)[number];

export const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  text: 'Строка',
  textarea: 'Текст',
  number: 'Число',
  digits: 'Номер (только цифры)',
  money: 'Сумма, ₽',
  date: 'Дата',
  select: 'Выбор из списка',
  user: 'Сотрудник',
  department: 'Отдел',
  checkbox: 'Да / нет',
  document: 'Связанный документ',
  table: 'Таблица',
};

export const COLUMN_TYPES = ['text', 'number', 'digits', 'money', 'date'] as const;
export type ColumnType = (typeof COLUMN_TYPES)[number];

export const columnSchema = z.object({ key, label: z.string().min(1).max(100), type: z.enum(COLUMN_TYPES) });

export const fieldSchema = z.object({
  key,
  label: z.string().min(1, 'Укажите название').max(200),
  type: z.enum(FIELD_TYPES),
  required: z.boolean().default(false),
  hint: z.string().max(500).optional(),
  options: z.array(z.string().min(1).max(200)).max(50).optional(),
  columns: z.array(columnSchema).max(10).optional(),
  /** Поле встроенного типа: удалить или сменить его вид нельзя. */
  system: z.boolean().optional(),
  /** Заполняет участник согласования (шаг с «Заполнить поля»), а не автор: в форме автора скрыто. */
  byApprover: z.boolean().optional(),
  /**
   * Показывать поле, только если условие по другим полям выполнено (например, «Тип контрагента» = ФЛ).
   * Скрытое поле не обязательно, его значение не сохраняется.
   */
  showIf: conditionSchema.optional(),
  /** Для «Связанного документа»: из каких видов заявок (ключи) выбирать — например, только договоры. Пусто — любые. */
  documentTypes: z.array(key).max(20).optional(),
});
export type FieldDefinition = z.infer<typeof fieldSchema>;
export type FieldDefinitionInput = z.input<typeof fieldSchema>;

export const REQUEST_BEHAVIORS = ['generic', 'trip', 'vacation'] as const;
export type RequestBehavior = (typeof REQUEST_BEHAVIORS)[number];

/** Когда формируется документ по шаблону: по кнопке, при каждом сохранении черновика, после согласования. */
export const TEMPLATE_WHEN = ['manual', 'on_save', 'on_approved'] as const;
export type TemplateWhen = (typeof TEMPLATE_WHEN)[number];

/** Готовые иконки типов заявок — в том же стиле, что у встроенных. */
export const TYPE_ICON_PRESETS = [
  '📄', '📝', '📑', '📋', '🗂', '📁', '📦', '🧾', '💰', '💳', '🏦', '📊', '📈', '🧮', '🖨', '💻',
  '🖥', '📱', '🔧', '🛠', '🚗', '🚚', '✈️', '🏨', '🌴', '🏖', '🩺', '💊', '🎓', '📚', '👥', '🤝',
  '👤', '📅', '⏰', '📢', '✉️', '📬', '⚖️', '🔒', '🔑', '🏢', '🏗', '🍽', '☕', '🎉', '✅', '⭐',
] as const;
export const DEFAULT_TYPE_ICON = '🗂';
/** Своя картинка хранится в базе как data:image/…;base64 — до этого размера. */
export const TYPE_ICON_MAX_LENGTH = 60_000;

const iconSchema = z
  .string()
  .trim()
  .max(TYPE_ICON_MAX_LENGTH, 'Картинка слишком большая')
  .refine(
    (v) => /^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/]+=*$/.test(v) || (v.length > 0 && v.length <= 16 && !/[<>"'&\s]/.test(v)),
    'Иконка — эмодзи (до 16 символов) или картинка PNG, JPEG, WebP',
  );

export const templateUseSchema = z.object({
  templateId: z.uuid(),
  when: z.enum(TEMPLATE_WHEN),
  condition: conditionSchema.optional(),
});
export type TemplateUse = { templateId: string; when: TemplateWhen; condition?: Condition };

export const requestTypeInputSchema = z
  .object({
    key,
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().max(1000).nullable().optional(),
    icon: iconSchema.nullable().optional(),
    /** Шаблон названия документа; пусто — «{{route}}: первое поле». */
    titleTemplate: z.string().trim().max(300).nullable().optional(),
    fields: z.array(fieldSchema).max(60),
    defaultRouteId: z.uuid().nullable().optional(),
    /** Может ли автор выбрать другой маршрут. */
    allowRouteChoice: z.boolean().default(true),
    /** Автор может собрать свой маршрут для документа: этапы и конкретные сотрудники. */
    allowCustomRoute: z.boolean().default(false),
    /**
     * Маршруты, из которых выбирает автор. Пусто — маршруты, не закреплённые ни за одним
     * типом (так маршруты командировок не попадают в выбор у договоров).
     */
    routeIds: z.array(z.uuid()).max(100).default([]),
    /**
     * Шаблоны: когда формировать и при каком условии. Условие — по полям заявки, например
     * «Тип контрагента = Физлицо» → свой шаблон договора; без условия — всегда.
     */
    templates: z.array(templateUseSchema).max(20).default([]),
    isActive: z.boolean().default(true),
  })
  .superRefine((t, ctx) => {
    const seen = new Set<string>();
    t.fields.forEach((f, i) => {
      if (seen.has(f.key)) ctx.addIssue({ code: 'custom', path: ['fields', i, 'key'], message: 'Ключ поля повторяется' });
      seen.add(f.key);
      if (f.type === 'select' && !f.options?.length) ctx.addIssue({ code: 'custom', path: ['fields', i, 'options'], message: 'Добавьте варианты' });
      if (f.type === 'table' && !f.columns?.length) ctx.addIssue({ code: 'custom', path: ['fields', i, 'columns'], message: 'Добавьте колонки' });
    });
  });
export type RequestTypeInput = z.input<typeof requestTypeInputSchema>;

export interface RequestTypeDto {
  id: string;
  key: string;
  name: string;
  description: string | null;
  /** Эмодзи или data:image/… ; null — общая иконка. */
  icon: string | null;
  titleTemplate: string | null;
  behavior: RequestBehavior;
  system: boolean;
  fields: FieldDefinition[];
  defaultRouteId: string | null;
  allowRouteChoice: boolean;
  allowCustomRoute: boolean;
  routeIds: string[];
  templates: TemplateUse[];
  isActive: boolean;
  /** Поля, которые считает система (для подсказок в шаблонах и условиях маршрутов). */
  computed: Array<{ key: string; label: string }>;
  /** Параметры расчёта — чтобы интерфейс пересчитывал смету при вводе. */
  calc: CalcSettings | null;
}

/**
 * Маршруты, доступные автору заявки этого типа: закреплённые за типом, а если таких нет —
 * не закреплённые ни за одним типом. Маршрут по умолчанию доступен всегда.
 */
export function allowedRouteIds(
  type: Pick<RequestTypeDto, 'id' | 'routeIds' | 'defaultRouteId' | 'allowRouteChoice'> | null,
  allTypes: ReadonlyArray<Pick<RequestTypeDto, 'id' | 'routeIds'>>,
  allRouteIds: readonly string[],
): string[] {
  if (type && !type.allowRouteChoice) return type.defaultRouteId ? [type.defaultRouteId] : [];
  let ids: string[];
  if (type && type.routeIds.length > 0) ids = [...type.routeIds];
  else {
    const bound = new Set(allTypes.filter((t) => t.id !== type?.id).flatMap((t) => t.routeIds));
    ids = allRouteIds.filter((id) => !bound.has(id));
  }
  if (type?.defaultRouteId && !ids.includes(type.defaultRouteId)) ids.unshift(type.defaultRouteId);
  return ids.filter((id) => allRouteIds.includes(id));
}

// ---------------------------------------------------------------------------
// Значения полей
// ---------------------------------------------------------------------------

export type TableRow = Record<string, string | number | null>;
export type FieldValue = string | number | boolean | null | TableRow[];
export type FieldValues = Record<string, FieldValue>;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isEmpty(v: unknown): boolean {
  return v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
}

/** Номер счёта, ИНН, БИК, КПП: цифры хранятся строкой — точно и с ведущими нулями. */
const DIGITS = /^\d{1,50}$/;
/** Наибольшее число, которое хранится без потери цифр (15 значащих). */
export const MAX_EXACT_NUMBER = 999_999_999_999_999;

function checkScalar(type: FieldType | ColumnType, v: unknown, options?: string[]): string | null {
  switch (type) {
    case 'text':
    case 'textarea':
      return typeof v === 'string' && v.length <= 20_000 ? null : 'Ожидается текст';
    case 'number':
    case 'money':
      if (typeof v !== 'number' || !Number.isFinite(v)) return 'Ожидается число';
      // Больше 15 цифр число хранится неточно: номер счёта превратился бы в 3.1e+21.
      return Math.abs(v) <= MAX_EXACT_NUMBER ? null : 'Слишком длинное число. Для номеров счетов, ИНН, БИК выберите вид поля «Номер (только цифры)»';
    case 'digits':
      return typeof v === 'string' && DIGITS.test(v) ? null : 'Только цифры';
    case 'date':
      return typeof v === 'string' && ISO_DATE.test(v) && !Number.isNaN(Date.parse(v)) ? null : 'Ожидается дата';
    case 'select':
      return typeof v === 'string' && (options ?? []).includes(v) ? null : 'Выберите значение из списка';
    case 'user':
    case 'department':
    case 'document':
      return typeof v === 'string' && UUID.test(v) ? null : 'Некорректное значение';
    case 'checkbox':
      return typeof v === 'boolean' ? null : 'Ожидается да или нет';
    case 'table':
      return 'Некорректное значение';
  }
}

/**
 * Проверка значений по описанию полей. Без forLaunch — только типы (черновик можно
 * сохранять незаполненным), с forLaunch — ещё и обязательность.
 */
/**
 * Поля, которые сейчас показываются: условие «показывать, только если» выполнено.
 * Условие может ссылаться на поле, которое само скрыто, — тогда его значение не учитывается.
 */
export function shownFields<F extends Pick<FieldDefinition, 'key' | 'showIf'>>(defs: F[], values: Record<string, unknown>): F[] {
  let hidden = new Set<string>();
  for (let pass = 0; pass <= defs.length; pass++) {
    const visible = Object.fromEntries(Object.entries(values).filter(([k]) => !hidden.has(k)));
    const next = new Set(defs.filter((f) => f.showIf && !evaluateCondition(f.showIf, visible)).map((f) => f.key));
    if (next.size === hidden.size && [...next].every((k) => hidden.has(k))) break;
    hidden = next;
  }
  return defs.filter((f) => !hidden.has(f.key));
}

/** Убирает значения скрытых полей: например, паспорт, если контрагент — юрлицо. */
export function dropHiddenFields<T extends Record<string, unknown>>(defs: FieldDefinition[], values: T): T {
  const shown = new Set(shownFields(defs, values).map((f) => f.key));
  const hidden = new Set(defs.filter((f) => !shown.has(f.key)).map((f) => f.key));
  if (hidden.size === 0) return values;
  return Object.fromEntries(Object.entries(values).filter(([k]) => !hidden.has(k))) as T;
}

export function validateFields(defs: FieldDefinition[], values: Record<string, unknown>, opts: { forLaunch: boolean }): Record<string, string> {
  const errors: Record<string, string> = {};
  // Скрытые по условию поля не проверяются и не обязательны.
  for (const f of shownFields(defs, values)) {
    const v = values[f.key];
    if (isEmpty(v)) {
      // Поле согласующего к запуску не требуется — его заполнят на шаге маршрута.
      if (opts.forLaunch && f.required && !f.byApprover) errors[f.key] = `Заполните поле «${f.label}»`;
      continue;
    }
    if (f.type === 'table') {
      if (!Array.isArray(v) || v.length > 200) {
        errors[f.key] = 'Некорректная таблица';
        continue;
      }
      for (const [i, row] of v.entries()) {
        if (typeof row !== 'object' || row === null) {
          errors[f.key] = `Строка ${i + 1}: некорректные данные`;
          break;
        }
        for (const col of f.columns ?? []) {
          const cell = (row as Record<string, unknown>)[col.key];
          if (isEmpty(cell)) continue;
          const err = checkScalar(col.type, cell);
          if (err) errors[f.key] = `Строка ${i + 1}, «${col.label}»: ${err.toLowerCase()}`;
        }
      }
      continue;
    }
    const err = checkScalar(f.type, v, f.options);
    if (err) errors[f.key] = err;
  }
  return errors;
}

/** Оставляет только описанные поля (и строки таблиц — только описанные колонки). */
export function pickFields(defs: FieldDefinition[], values: Record<string, unknown>): FieldValues {
  const out: FieldValues = {};
  for (const f of defs) {
    const v = values[f.key];
    if (v === undefined) continue;
    if (f.type === 'table' && Array.isArray(v)) {
      out[f.key] = v.map((row) => {
        const r: TableRow = {};
        for (const c of f.columns ?? []) {
          const cell = (row as Record<string, unknown>)?.[c.key];
          r[c.key] = typeof cell === 'string' || typeof cell === 'number' ? cell : null;
        }
        return r;
      });
    } else if (f.type === 'digits' && typeof v === 'number') {
      // Поле было «Числом», а стало «Номером»: прежнее значение — строкой цифр.
      out[f.key] = Number.isInteger(v) && v >= 0 ? BigInt(v).toString() : String(v);
    } else {
      out[f.key] = v as FieldValue;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Расчёты встроенных типов
// ---------------------------------------------------------------------------

// ---- Командировки: нормы ----

export const TRIP_KIND_DOMESTIC = 'По России';
export const TRIP_KIND_FOREIGN = 'Заграничная';

/** Валюты норм: названия — как в постановлениях. */
export const CURRENCIES: Record<string, { name: string; sign: string }> = {
  USD: { name: 'доллары США', sign: '$' },
  EUR: { name: 'евро', sign: '€' },
  GBP: { name: 'английские фунты стерлингов', sign: '£' },
  CHF: { name: 'швейцарские франки', sign: 'CHF' },
  DKK: { name: 'датские кроны', sign: 'DKK' },
  NOK: { name: 'норвежские кроны', sign: 'NOK' },
  SEK: { name: 'шведские кроны', sign: 'SEK' },
  JPY: { name: 'японские иены', sign: '¥' },
  CNY: { name: 'китайские юани', sign: '¥' },
};

export const currencySign = (code: string | null | undefined) => (code ? (CURRENCIES[code]?.sign ?? code) : '');

/** Код валюты по названию из постановления («доллары США» → USD). */
export function currencyByName(text: string): string | null {
  const t = text.trim().toLowerCase().replace(/ё/g, 'е');
  if (/^[a-z]{3}$/.test(t)) return t.toUpperCase();
  for (const [code, c] of Object.entries(CURRENCIES)) if (t === c.name) return code;
  if (t.startsWith('доллар')) return 'USD';
  if (t.startsWith('евро')) return 'EUR';
  if (t.includes('фунт')) return 'GBP';
  if (t.includes('франк')) return 'CHF';
  if (t.includes('датск')) return 'DKK';
  if (t.includes('норвежск')) return 'NOK';
  if (t.includes('шведск')) return 'SEK';
  if (t.includes('иен') || t.includes('йен')) return 'JPY';
  if (t.includes('юан')) return 'CNY';
  return null;
}

const currencyCode = z.string().regex(/^[A-Z]{3}$/, 'Код валюты из трёх латинских букв');

/** Норма для страны (или города внутри страны) в валюте постановления. */
export const foreignNormSchema = z.object({
  country: z.string().trim().min(1, 'Укажите страну').max(200),
  /** Суточные: Приложение N 1 к ПП РФ N 812, 1-й столбец. */
  perDiem: z.number().min(0).max(1_000_000).nullable(),
  perDiemCurrency: currencyCode.default('USD'),
  /** Проживание в сутки: приложение к ПП РФ N 1267. */
  lodging: z.number().min(0).max(10_000_000).nullable(),
  lodgingCurrency: currencyCode.default('USD'),
});
export type ForeignNorm = z.output<typeof foreignNormSchema>;

export interface CalcSettings {
  /** Суточные по России, ₽ в день. */
  perDiemRate: number;
  /** Проживание по России, ₽ в сутки. */
  lodgingRate: number;
  /** Заграничные нормы. */
  norms: ForeignNorm[];
  /** Курсы ЦБ РФ: ₽ за 1 единицу валюты, и дата курсов. */
  rates: Record<string, number>;
  ratesDate: string | null;
  /**
   * За границей — единый тариф для всех стран вместо норматива страны, ₽ в день / в сутки.
   * null — по нормативу (ПП № 812 и № 1267) в валюте по курсу ЦБ РФ.
   */
  foreignPerDiemFlat?: number | null;
  foreignLodgingFlat?: number | null;
}

/** Режим расчёта за границей: по нормативу страны или по единому тарифу. */
export const FOREIGN_CALC_MODES = ['norm', 'flat'] as const;
export type ForeignCalcMode = (typeof FOREIGN_CALC_MODES)[number];

const normKey = (s: string) => s.trim().toLowerCase().replace(/ё/g, 'е').replace(/\s+/g, ' ');

/**
 * Норма для выбранной страны или города. Суточные (ПП № 812) заданы по странам, а проживание
 * (ПП № 1267) — иногда по городам, поэтому недостающее значение берётся у «родственной» строки:
 * для «Германия — Берлин» суточные — у «Германия»; для «Германия» проживание — у «Германия — прочие города».
 */
export function findNorm(norms: readonly ForeignNorm[], country: unknown): ForeignNorm | null {
  if (typeof country !== 'string' || !country.trim()) return null;
  const k = normKey(country);
  const own = norms.find((n) => normKey(n.country) === k);
  if (!own) return null;
  const sep = own.country.indexOf(' — ');
  const parentName = sep > 0 ? normKey(own.country.slice(0, sep)) : null;
  const parent = parentName ? norms.find((n) => normKey(n.country) === parentName) : null;
  const others = !parentName ? norms.find((n) => /^(прочие|другие|остальные)/.test(normKey(n.country).split(' — ')[1] ?? '') && normKey(n.country).startsWith(`${k} — `)) : null;
  const pd = own.perDiem != null ? own : parent?.perDiem != null ? parent : null;
  const lg = own.lodging != null ? own : parent?.lodging != null ? parent : others?.lodging != null ? others : null;
  return {
    country: own.country,
    perDiem: pd?.perDiem ?? null,
    perDiemCurrency: pd?.perDiemCurrency ?? own.perDiemCurrency,
    lodging: lg?.lodging ?? null,
    lodgingCurrency: lg?.lodgingCurrency ?? own.lodgingCurrency,
  };
}

/** Нужен ли норматив страны: хотя бы что-то считается не по единому тарифу. */
export function needsForeignNorm(settings: Pick<CalcSettings, 'foreignPerDiemFlat' | 'foreignLodgingFlat'>): boolean {
  return settings.foreignPerDiemFlat == null || settings.foreignLodgingFlat == null;
}

/** Какие курсы нужны для заграничной командировки и каких не хватает (по единому тарифу курс не нужен). */
export function missingRates(norm: ForeignNorm | null, rates: Record<string, number>, flat: Pick<CalcSettings, 'foreignPerDiemFlat' | 'foreignLodgingFlat'> = {}): string[] {
  if (!norm) return [];
  const need = [
    norm.perDiem != null && flat.foreignPerDiemFlat == null ? norm.perDiemCurrency : null,
    norm.lodging != null && flat.foreignLodgingFlat == null ? norm.lodgingCurrency : null,
  ].filter((c): c is string => Boolean(c));
  return [...new Set(need)].filter((c) => !((rates[c] ?? 0) > 0));
}

function days(from: unknown, to: unknown): { total: number; weekend: number; dates: string[] } | null {
  if (typeof from !== 'string' || typeof to !== 'string' || !ISO_DATE.test(from) || !ISO_DATE.test(to)) return null;
  const start = Date.UTC(+from.slice(0, 4), +from.slice(5, 7) - 1, +from.slice(8, 10));
  const end = Date.UTC(+to.slice(0, 4), +to.slice(5, 7) - 1, +to.slice(8, 10));
  if (end < start) return null;
  const dates: string[] = [];
  let weekend = 0;
  for (let t = start; t <= end && dates.length < 366; t += 86_400_000) {
    const d = new Date(t);
    if (d.getUTCDay() === 0 || d.getUTCDay() === 6) weekend += 1;
    dates.push(d.toISOString().slice(0, 10));
  }
  return { total: dates.length, weekend, dates };
}

const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const money = (v: number) => Math.round(v * 100) / 100;

export const COMPUTED_FIELDS: Record<RequestBehavior, Array<{ key: string; label: string }>> = {
  generic: [],
  trip: [
    { key: 'days', label: 'Дней в командировке' },
    { key: 'weekendDays', label: 'Из них выходных' },
    { key: 'workDays', label: 'Рабочих дней' },
    { key: 'nights', label: 'Ночей (проживание)' },
    { key: 'foreignDays', label: 'Дней по норме страны' },
    { key: 'domesticDays', label: 'Дней по норме России (день возвращения)' },
    { key: 'perDiemDomesticRate', label: 'Суточные по России, ₽ в день' },
    { key: 'perDiemNorm', label: 'Норма суточных в валюте, в день' },
    { key: 'perDiemCurrency', label: 'Валюта суточных' },
    { key: 'perDiemCurrencyRate', label: 'Курс валюты суточных ЦБ РФ, ₽' },
    { key: 'lodgingNorm', label: 'Норма проживания в валюте, в сутки' },
    { key: 'lodgingCurrency', label: 'Валюта проживания' },
    { key: 'lodgingCurrencyRate', label: 'Курс валюты проживания ЦБ РФ, ₽' },
    { key: 'ratesDate', label: 'Дата курсов' },
    { key: 'perDiemForeignFlat', label: 'Суточные за границей по единому тарифу, ₽ в день' },
    { key: 'lodgingForeignFlat', label: 'Проживание за границей по единому тарифу, ₽ в сутки' },
    { key: 'perDiemRate', label: 'Суточные, ₽ в день' },
    { key: 'lodgingRate', label: 'Проживание, ₽ в сутки' },
    { key: 'perDiemCalc', label: 'Суточные (расчёт)' },
    { key: 'perDiem', label: 'Суточные (итог)' },
    { key: 'lodgingCalc', label: 'Проживание (расчёт)' },
    { key: 'lodgingTotal', label: 'Проживание (итог)' },
    { key: 'travelAdvance', label: 'Проезд — в подотчёт' },
    { key: 'travelInvoice', label: 'Проезд — по счёту' },
    { key: 'lodgingAdvance', label: 'Проживание — в подотчёт' },
    { key: 'lodgingInvoice', label: 'Проживание — по счёту' },
    { key: 'otherAdvance', label: 'Прочие — в подотчёт' },
    { key: 'otherInvoice', label: 'Прочие — по счёту' },
    { key: 'advanceTotal', label: 'Итого в подотчёт (с суточными)' },
    { key: 'invoiceTotal', label: 'Итого по счетам' },
    { key: 'total', label: 'Итого по смете' },
  ],
  vacation: [
    { key: 'days', label: 'Календарных дней' },
    { key: 'weekendDays', label: 'Из них выходных' },
  ],
};

/**
 * Правило дня пересечения границы (Положение о командировках, ПП РФ от 13.10.2008 N 749, п. 18–20):
 * день выезда из России оплачивается по норме страны, день возвращения — как по России.
 * Если уехал и вернулся в тот же день — половина нормы страны.
 * Возвращает число дней по норме страны (дробное — для однодневной) и по российской ставке.
 */
export function borderDays(total: number): { foreign: number; domestic: number } {
  if (total <= 0) return { foreign: 0, domestic: 0 };
  if (total === 1) return { foreign: 0.5, domestic: 0 };
  return { foreign: total - 1, domestic: 1 };
}

/** Расчётные поля командировки, которые являются суммами в рублях. */
export const MONEY_COMPUTED_FIELDS: ReadonlySet<string> = new Set([
  'perDiemRate',
  'perDiemDomesticRate',
  'perDiemForeignFlat',
  'lodgingForeignFlat',
  'lodgingRate',
  'perDiemCalc',
  'perDiem',
  'lodgingCalc',
  'lodgingTotal',
  'travelAdvance',
  'travelInvoice',
  'lodgingAdvance',
  'lodgingInvoice',
  'otherAdvance',
  'otherInvoice',
  'advanceTotal',
  'invoiceTotal',
  'total',
]);

// ---- Как оплачиваются расходы командировки ----

export const PAY_ADVANCE = 'В подотчёт' as const;
export const PAY_INVOICE = 'По счёту' as const;
export const PAY_METHODS = [PAY_ADVANCE, PAY_INVOICE] as const;
/** Статьи, у которых выбирается способ оплаты (суточные всегда в подотчёт). */
export const PAY_EXPENSES = [
  { key: 'travel', label: 'Проезд' },
  { key: 'lodging', label: 'Проживание' },
  { key: 'other', label: 'Прочие расходы' },
] as const;
export type PayExpenseKey = (typeof PAY_EXPENSES)[number]['key'];

export interface PaymentPart {
  method: (typeof PAY_METHODS)[number];
  amount: number;
}

const payLabel = (key: PayExpenseKey) => PAY_EXPENSES.find((e) => e.key === key)!.label;
const payMethod = (r: TableRow) => (r.method === PAY_INVOICE ? PAY_INVOICE : PAY_ADVANCE);

/** Строки статьи в «Расходы и оплата»: каждая — сумма и способ оплаты. */
export function expenseRows(values: Record<string, unknown>, key: PayExpenseKey): TableRow[] {
  return (Array.isArray(values.payments) ? (values.payments as TableRow[]) : []).filter((r) => r && r.expense === payLabel(key));
}

/**
 * Суммы статей — из строк «Расходы и оплата» (их инициатор вводит один раз):
 * проезд и прочие — сумма строк, проживание — сумма строк, а если суммы не введены — по норме.
 * Заявки без строк (старые) считаются по прежним полям.
 */
export function applyPayments(values: Record<string, unknown>): Record<string, unknown> {
  if (!Array.isArray(values.payments)) return values;
  const next = { ...values };
  for (const e of PAY_EXPENSES) {
    const rows = expenseRows(values, e.key);
    const amounts = rows.map((r) => r.amount).filter((v): v is number => typeof v === 'number');
    const field = e.key === 'lodging' ? 'lodging' : e.key;
    next[field] = amounts.length ? money(amounts.reduce((s, v) => s + v, 0)) : null;
  }
  return next;
}

/** Части оплаты статьи. Проживание без введённых сумм — по норме, способом из его строки. */
export function paymentParts(values: Record<string, unknown>, key: PayExpenseKey, total: number): PaymentPart[] {
  const rows = expenseRows(values, key);
  const entered = rows.filter((r) => typeof r.amount === 'number' && r.amount > 0);
  if (entered.length) return entered.map((r) => ({ method: payMethod(r), amount: money(r.amount as number) }));
  if (total <= 0) return [];
  return [{ method: rows.length ? payMethod(rows[rows.length - 1]!) : PAY_ADVANCE, amount: money(total) }];
}

/**
 * Все выплаты командировки строками — для шаблона служебной записки ({{#payments}}…{{/payments}}):
 * суточные (всегда в подотчёт), затем проезд, проживание и прочие по частям.
 */
export function tripPaymentRows(values: Record<string, unknown>): TableRow[] {
  const perDiem = num(values.perDiem);
  const rows: TableRow[] = perDiem > 0 ? [{ expense: 'Суточные', method: PAY_ADVANCE, amount: perDiem }] : [];
  for (const e of PAY_EXPENSES) {
    const total = e.key === 'lodging' ? num(values.lodgingTotal) : num(values[e.key]);
    for (const p of paymentParts(values, e.key, total)) rows.push({ expense: e.label, method: p.method, amount: p.amount });
  }
  return rows;
}

/** Отметка «курс зафиксирован при согласовании»: расчёт больше не меняется с курсом. */
export const RATE_FIXED_FIELD = 'ratesFixed';

export function isForeignTrip(values: Record<string, unknown>): boolean {
  return values.tripKind === TRIP_KIND_FOREIGN;
}

/**
 * Расчётные значения. Ручные значения хранятся отдельно (perDiemManual, lodging):
 * если автор исправил сумму вручную, расчёт её не затирает, а итог берёт ручную.
 *
 * По России: суточные — ставка × дни, проживание — ставка × ночи (на одну меньше дней).
 * За границей: нормы страны в валюте постановления пересчитываются в рубли по курсу ЦБ РФ.
 */
export function computeFields(behavior: RequestBehavior, values: Record<string, unknown>, settings: CalcSettings): Record<string, number | string | null> {
  if (behavior === 'generic') return {};
  const span = days(values.dateFrom, values.dateTo);
  if (behavior === 'vacation') {
    return { days: span?.total ?? null, weekendDays: span?.weekend ?? null };
  }
  const nights = span ? Math.max(span.total - 1, 0) : null;
  const foreign = isForeignTrip(values);
  // Единый тариф для всех стран (настройка): по нему норматив страны и курс не нужны.
  const pdFlat = foreign ? (settings.foreignPerDiemFlat ?? null) : null;
  const lgFlat = foreign ? (settings.foreignLodgingFlat ?? null) : null;
  const found = foreign ? findNorm(settings.norms, values.country) : null;
  const norm = found && { ...found, perDiem: pdFlat === null ? found.perDiem : null, lodging: lgFlat === null ? found.lodging : null };
  const pdRate = norm?.perDiem != null ? (settings.rates[norm.perDiemCurrency] ?? null) : null;
  const lgRate = norm?.lodging != null ? (settings.rates[norm.lodgingCurrency] ?? null) : null;

  let perDiemRate: number | null;
  let lodgingRate: number | null;
  let perDiemCalc: number | null = null;
  let lodgingCalc: number | null = null;
  const split = foreign && span ? borderDays(span.total) : null;
  if (foreign) {
    // Сначала сумма в валюте, затем пересчёт по курсу — без накопления округлений.
    perDiemRate = pdFlat ?? (norm?.perDiem != null && pdRate ? money(norm.perDiem * pdRate) : null);
    lodgingRate = lgFlat ?? (norm?.lodging != null && lgRate ? money(norm.lodging * lgRate) : null);
    // День возвращения — по ставке России и при едином тарифе (правило пересечения границы).
    if (split && pdFlat !== null) perDiemCalc = money(split.foreign * pdFlat + split.domestic * settings.perDiemRate);
    else if (split && norm?.perDiem != null && pdRate) {
      perDiemCalc = money(split.foreign * norm.perDiem * pdRate + split.domestic * settings.perDiemRate);
    }
    if (nights !== null && lgFlat !== null) lodgingCalc = money(nights * lgFlat);
    else if (nights !== null && norm?.lodging != null && lgRate) lodgingCalc = money(nights * norm.lodging * lgRate);
  } else {
    perDiemRate = settings.perDiemRate;
    lodgingRate = settings.lodgingRate;
    if (span) perDiemCalc = money(span.total * perDiemRate);
    if (nights !== null) lodgingCalc = money(nights * lodgingRate);
  }

  const perDiemManual = typeof values.perDiemManual === 'number' ? values.perDiemManual : null;
  const lodgingManual = typeof values.lodging === 'number' ? values.lodging : null;
  const perDiem = perDiemManual ?? perDiemCalc;
  const lodgingTotal = lodgingManual ?? lodgingCalc;
  const total = span ? money(num(perDiem) + num(values.travel) + num(lodgingTotal) + num(values.other)) : null;
  // Способ оплаты: суточные — в подотчёт, остальное — как выбрал инициатор (по умолчанию в подотчёт).
  const sums = (key: PayExpenseKey, amount: number) => {
    const parts = paymentParts(values, key, amount);
    const of = (m: string) => money(parts.filter((p) => p.method === m).reduce((s, p) => s + p.amount, 0));
    return { advance: of(PAY_ADVANCE), invoice: of(PAY_INVOICE) };
  };
  const tr = sums('travel', num(values.travel));
  const lg = sums('lodging', num(lodgingTotal));
  const ot = sums('other', num(values.other));
  return {
    days: span?.total ?? null,
    weekendDays: span?.weekend ?? null,
    workDays: span ? span.total - span.weekend : null,
    nights,
    foreignDays: split?.foreign ?? null,
    domesticDays: split?.domestic ?? null,
    perDiemDomesticRate: settings.perDiemRate,
    perDiemNorm: norm?.perDiem ?? null,
    perDiemCurrency: norm?.perDiem != null ? norm.perDiemCurrency : null,
    perDiemCurrencyRate: norm?.perDiem != null ? pdRate : null,
    lodgingNorm: norm?.lodging ?? null,
    lodgingCurrency: norm?.lodging != null ? norm.lodgingCurrency : null,
    lodgingCurrencyRate: norm?.lodging != null ? lgRate : null,
    ratesDate: foreign && (norm?.perDiem != null || norm?.lodging != null) ? settings.ratesDate : null,
    perDiemForeignFlat: pdFlat,
    lodgingForeignFlat: lgFlat,
    perDiemRate,
    lodgingRate,
    perDiemCalc,
    perDiem,
    lodgingCalc,
    lodgingTotal,
    travelAdvance: span ? tr.advance : null,
    travelInvoice: span ? tr.invoice : null,
    lodgingAdvance: span ? lg.advance : null,
    lodgingInvoice: span ? lg.invoice : null,
    otherAdvance: span ? ot.advance : null,
    otherInvoice: span ? ot.invoice : null,
    advanceTotal: span ? money(num(perDiem) + tr.advance + lg.advance + ot.advance) : null,
    invoiceTotal: span ? money(tr.invoice + lg.invoice + ot.invoice) : null,
    total,
  };
}

/**
 * Настройки расчёта, восстановленные из сохранённой заявки: так карточка показывает
 * ровно то, что посчитал сервер (нормы и курс на момент расчёта).
 */
export function calcFromValues(values: Record<string, unknown>): CalcSettings {
  const n = (v: unknown) => (typeof v === 'number' ? v : null);
  const str = (v: unknown) => (typeof v === 'string' ? v : null);
  const country = str(values.country) ?? '';
  const pdCur = str(values.perDiemCurrency) ?? 'USD';
  const lgCur = str(values.lodgingCurrency) ?? 'USD';
  const rates: Record<string, number> = {};
  if (n(values.lodgingCurrencyRate)) rates[lgCur] = n(values.lodgingCurrencyRate)!;
  if (n(values.perDiemCurrencyRate)) rates[pdCur] = n(values.perDiemCurrencyRate)!;
  return {
    perDiemRate: n(values.perDiemDomesticRate) ?? n(values.perDiemRate) ?? 0,
    lodgingRate: n(values.lodgingRate) ?? 0,
    norms: country ? [{ country, perDiem: n(values.perDiemNorm), perDiemCurrency: pdCur, lodging: n(values.lodgingNorm), lodgingCurrency: lgCur }] : [],
    rates,
    ratesDate: str(values.ratesDate),
    foreignPerDiemFlat: n(values.perDiemForeignFlat),
    foreignLodgingFlat: n(values.lodgingForeignFlat),
  };
}

/** Дни командировки. */
export function tripDates(values: Record<string, unknown>): string[] {
  return days(values.dateFrom, values.dateTo)?.dates ?? [];
}

export const WEEKDAYS = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'] as const;

export function weekdayOf(date: string): string {
  return WEEKDAYS[new Date(`${date}T12:00:00Z`).getUTCDay()]!;
}

/**
 * Расписание командировки — строка на каждый день: дата и день недели ставятся сами,
 * уже написанное по этой дате сохраняется. Без дат расписание не трогаем.
 */
export function syncSchedule(values: Record<string, unknown>): TableRow[] | null {
  const dates = tripDates(values);
  if (dates.length === 0) return null;
  const old = Array.isArray(values.schedule) ? (values.schedule as TableRow[]) : [];
  return dates.map((date) => {
    const prev = old.find((r) => r?.date === date) ?? {};
    return { ...prev, date, weekday: weekdayOf(date), description: typeof prev.description === 'string' ? prev.description : '' };
  });
}

/** Дополнительные проверки встроенных типов при запуске. */
export function behaviorErrors(behavior: RequestBehavior, values: Record<string, unknown>, settings?: CalcSettings): Record<string, string> {
  const errors: Record<string, string> = {};
  // Поля «С» и «По» в любом типе: окончание не раньше начала.
  if (typeof values.dateFrom === 'string' && typeof values.dateTo === 'string' && values.dateTo < values.dateFrom) {
    errors.dateTo = 'Дата окончания раньше даты начала';
  }
  // Замещение: сотрудник не замещает сам себя.
  if (typeof values.deputy === 'string' && values.deputy && values.deputy === values.employee) {
    errors.deputy = 'Сотрудник не может замещать сам себя';
  }
  if (behavior === 'generic') return errors;
  if (behavior === 'trip' && isForeignTrip(values) && settings) {
    const norm = findNorm(settings.norms, values.country);
    if (!values.country) errors.country = 'Укажите страну заграничной командировки';
    else if (!norm && needsForeignNorm(settings)) errors.country = 'Для этой страны нет норм — выберите страну из списка или попросите администратора добавить её в настройках';
    const missing = missingRates(norm, settings.rates, settings);
    if (missing.length) errors.tripKind = `Не задан курс ${missing.map((c) => CURRENCIES[c]?.name ?? c).join(', ')} — администратор задаёт курсы в настройках`;
  }
  return errors;
}

// ---------------------------------------------------------------------------
// Встроенные типы
// ---------------------------------------------------------------------------

const sys = (f: FieldDefinitionInput): FieldDefinitionInput => ({ ...f, system: true });

export const BUILTIN_TYPES: Array<{ key: string; name: string; description: string; icon: string; behavior: RequestBehavior; fields: FieldDefinitionInput[] }> = [
  {
    key: 'document',
    name: 'Документ',
    icon: '📄',
    description: 'Произвольный документ на согласование: договор, служебная записка, приказ',
    behavior: 'generic',
    fields: [],
  },
  {
    key: 'trip',
    name: 'Командировка',
    icon: '✈️',
    description: 'Цель, даты, расписание и смета; после согласования — приказ и смета по шаблонам',
    behavior: 'trip',
    fields: [
      sys({ key: 'employee', label: 'Сотрудник', type: 'user', required: true, hint: 'Кто едет. Запустить за другого можно при наличии права' }),
      sys({ key: 'position', label: 'Должность', type: 'text', required: true, hint: 'Из карточки сотрудника' }),
      sys({
        key: 'tripKind',
        label: 'Вид командировки',
        type: 'select',
        required: true,
        options: [TRIP_KIND_DOMESTIC, TRIP_KIND_FOREIGN],
        hint: 'За границей суточные и проживание считаются по нормам страны в валюте по курсу ЦБ РФ',
      }),
      sys({ key: 'country', label: 'Страна', type: 'text', hint: 'Для заграничной командировки — из списка норм' }),
      sys({ key: 'destination', label: 'Место командировки', type: 'text', required: true }),
      sys({ key: 'purpose', label: 'Цель', type: 'textarea', required: true }),
      sys({ key: 'dateFrom', label: 'Дата начала', type: 'date', required: true }),
      sys({ key: 'dateTo', label: 'Дата окончания', type: 'date', required: true }),
      sys({
        key: 'weekendPay',
        label: 'Работа в выходные',
        type: 'select',
        options: ['Оплата в двойном размере', 'Одинарная оплата и день отдыха'],
        hint: 'Если командировка захватывает выходные',
      }),
      sys({
        key: 'schedule',
        label: 'Расписание по дням',
        type: 'table',
        columns: [
          { key: 'date', label: 'Дата', type: 'date' },
          { key: 'weekday', label: 'День недели', type: 'text' },
          { key: 'description', label: 'Что планируется', type: 'text' },
        ],
        hint: 'Строки на каждый день появляются сами по датам командировки',
      }),
      sys({ key: 'travel', label: 'Проезд', type: 'money' }),
      sys({ key: 'lodging', label: 'Проживание вручную', type: 'money', hint: 'Если сумма отличается от расчёта по норме' }),
      sys({ key: 'other', label: 'Прочие расходы', type: 'money' }),
      sys({ key: 'perDiemManual', label: 'Суточные вручную', type: 'money', hint: 'Если сумма отличается от расчёта' }),
      sys({
        key: 'payments',
        label: 'Расходы и оплата',
        type: 'table',
        columns: [
          { key: 'expense', label: 'Статья', type: 'text' },
          { key: 'method', label: 'Как оплачивается', type: 'text' },
          { key: 'amount', label: 'Сумма', type: 'money' },
        ],
        hint: 'Суммы проезда, проживания и прочих расходов и как они оплачиваются: в подотчёт сотруднику или по счёту организации. Статью можно разбить на строки',
      }),
    ],
  },
  {
    key: 'vacation',
    name: 'Отпуск',
    icon: '🌴',
    description: 'Заявление на отпуск с заместителем на время отсутствия',
    behavior: 'vacation',
    fields: [
      sys({ key: 'employee', label: 'Сотрудник', type: 'user', required: true }),
      sys({
        key: 'vacationKind',
        label: 'Вид отпуска',
        type: 'select',
        required: true,
        options: ['Ежегодный оплачиваемый', 'Без сохранения заработной платы', 'Учебный', 'Иной'],
      }),
      sys({ key: 'dateFrom', label: 'С', type: 'date', required: true }),
      sys({ key: 'dateTo', label: 'По', type: 'date', required: true }),
      sys({ key: 'replacement', label: 'Замещает на время отпуска', type: 'user' }),
      sys({ key: 'comment', label: 'Комментарий', type: 'textarea' }),
    ],
  },
  {
    key: 'substitution',
    name: 'Замещение',
    icon: '🔁',
    description: 'Заместитель на время отсутствия. Замещение назначает действие маршрута «Назначить замещение»',
    behavior: 'generic',
    fields: [
      sys({ key: 'employee', label: 'Кого замещают', type: 'user', required: true, hint: 'По умолчанию — вы. За другого — при наличии права' }),
      sys({ key: 'deputy', label: 'Заместитель', type: 'user', required: true }),
      sys({ key: 'dateFrom', label: 'С', type: 'date', required: true }),
      sys({ key: 'dateTo', label: 'По', type: 'date', required: true }),
      sys({ key: 'reason', label: 'Причина', type: 'textarea', hint: 'Например: отпуск, командировка, больничный' }),
    ],
  },
];

/** Поле «за кого» заявка: для командировки, отпуска и замещения — сотрудник. */
export const SUBJECT_FIELD = 'employee';

// ---------------------------------------------------------------------------
// Шаблоны документов
// ---------------------------------------------------------------------------

export const TEMPLATE_FORMATS = ['docx', 'xlsx'] as const;
export type TemplateFormat = (typeof TEMPLATE_FORMATS)[number];

export const templateMetaSchema = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(1000).nullable().optional(),
  /** document — основной документ задачи (его правят и согласуют), generated — сформированный (приказ, смета). */
  outputKind: z.enum(['document', 'generated']).default('generated'),
  /** Имя файла, можно с метками: «Приказ №{{number}}». */
  outputName: z.string().trim().min(1).max(200),
  isActive: z.boolean().default(true),
});
export type TemplateMetaInput = z.input<typeof templateMetaSchema>;

export interface TemplateDto {
  id: string;
  name: string;
  description: string | null;
  format: TemplateFormat;
  originalName: string;
  outputKind: 'document' | 'generated';
  outputName: string;
  placeholders: string[];
  isActive: boolean;
  version: number;
  updatedAt: string;
}

/**
 * Что ещё можно взять у сотрудника из поля «Сотрудник» (и у автора): {{sotrudnik.dolzhnost}},
 * {{sotrudnik.otdel}}, {{sotrudnik.fioKratko}}. Сама метка поля {{sotrudnik}} — ФИО полностью.
 */
export const USER_PLACEHOLDER_PARTS: Array<{ suffix: string; label: string; sample: string }> = [
  { suffix: '.dolzhnost', label: 'должность', sample: 'Инженер' },
  { suffix: '.otdel', label: 'отдел', sample: 'Отдел закупок' },
  { suffix: '.fioKratko', label: 'фамилия и инициалы', sample: 'Сидоров С.С.' },
  { suffix: '.inicialyFamiliya', label: 'инициалы и фамилия (для подписи)', sample: 'С.С. Сидоров' },
];

/** «Петухова Надежда Владимировна» → «Петухова Н.В.» (или «Н.В. Петухова»). */
export function shortName(full: string | null | undefined, initialsFirst = false): string {
  const [last, ...rest] = (full ?? '').trim().split(/\s+/).filter(Boolean);
  if (!last) return '';
  const initials = rest.map((p) => `${p[0]!.toUpperCase()}.`).join('');
  if (!initials) return last;
  return initialsFirst ? `${initials} ${last}` : `${last} ${initials}`;
}

/** Метки сотрудника: ключ поля + должность, отдел, сокращённое ФИО. */
export function userPlaceholders(key: string, u: { name: string; position: string | null; department: string | null } | undefined): Record<string, string> {
  return {
    [key]: u?.name ?? '',
    // Прежняя запись должности — шаблоны с ней продолжают работать.
    [`${key}Position`]: u?.position ?? '',
    [`${key}.dolzhnost`]: u?.position ?? '',
    [`${key}.otdel`]: u?.department ?? '',
    [`${key}.fioKratko`]: shortName(u?.name),
    [`${key}.inicialyFamiliya`]: shortName(u?.name, true),
  };
}

/** Метки, доступные во всех шаблонах. */
export const COMMON_PLACEHOLDERS: Array<{ key: string; label: string }> = [
  { key: 'number', label: 'Номер задачи' },
  { key: 'title', label: 'Название' },
  { key: 'description', label: 'Описание' },
  { key: 'today', label: 'Сегодняшняя дата' },
  { key: 'author', label: 'Автор (ФИО)' },
  { key: 'authorPosition', label: 'Должность автора' },
  { key: 'authorDepartment', label: 'Отдел автора' },
  { key: 'author.fioKratko', label: 'Автор: фамилия и инициалы' },
  { key: 'author.inicialyFamiliya', label: 'Автор: инициалы и фамилия (для подписи)' },
  { key: 'approvedAt', label: 'Дата согласования' },
  { key: '#approvers', label: 'Список согласовавших (цикл: name, position, decision, date)' },
];

export const generateSchema = z.object({ templateId: z.uuid() });
